import { useState, useEffect } from 'react';
import { get, put } from '../../../services/request';
import { warningNotification } from '../../../services/notification';
import { FormData } from '../types';
import { get as getStorage } from '../../../services/storage';
import { isBackofficeTestUser } from '../../../util';

export type FormState = {
  [key: string]: FormData;
};

interface UseNotificationEmailsProps {
  estateId?: string;
  category: string;
  subcategories: string[];
  defaultFormData: FormData;
}

export const useNotificationEmails = ({
  estateId,
  category,
  subcategories,
  defaultFormData
}: UseNotificationEmailsProps) => {
  const isBackofficeTest = isBackofficeTestUser({
    role: getStorage('role') || undefined
  });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormState>(() => {
    const initialData: FormState = {};
    subcategories.forEach(sub => {
      initialData[sub] = { ...defaultFormData };
    });
    return initialData;
  });

  const handleUpdateFormData = (sectionKey: string, updatedData: FormData) => {
    if (isBackofficeTest) return;

    setFormData(prev => ({
      ...prev,
      [sectionKey]: updatedData
    }));
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const data = await get(
          `/notification-emails/panel?estateId=${estateId}&category=${category}`
        );

        if (data) {
          const { emails: additionalRecipient, config: configData } = data;
          const defaultRecipients = additionalRecipient.filter(
            (recipient: any) => !recipient.subcategory
          );

          const buildFormData = (subcategory: string): FormData => {
            const filtered = additionalRecipient.filter(
              (recipient: any) => recipient.subcategory === subcategory
            );

            const configForSub = configData.find(
              (config: any) => config.subcategory === subcategory
            );

            if (filtered.length > 0) {
              return {
                ...defaultFormData,
                ...filtered[0],
                ...configForSub,
                additionalRecipient: filtered
                  .map((recipient: any) => recipient.email)
                  .concat(defaultRecipients.map((recipient: any) => recipient.email))
              };
            } else {
              return {
                ...defaultFormData,
                ...configForSub,
                additionalRecipient: defaultRecipients.map(
                  (recipient: any) => recipient.email
                )
              };
            }
          };

          const newFormData: FormState = {};
          subcategories.forEach(sub => {
            newFormData[sub] = buildFormData(sub);
          });

          setFormData(newFormData);
        } else {
          console.log('Não há dados para exibir');
        }
      } catch (error) {
        console.error('Erro ao buscar os dados:', error);
        warningNotification('Ops!', 'Erro ao buscar os dados');
      } finally {
        setLoading(false);
      }
    }

    if (estateId) {
      fetchData();
    }
  }, [estateId, category, subcategories, defaultFormData]);

  const handleSave = async () => {
    if (isBackofficeTest) return;

    try {
      setLoading(true);
      const notifications = Object.entries(formData).map(([subcategory, data]) => ({
        id: data.id || null,
        estate_id: estateId,
        category,
        subcategory,
        creationResponsible: data.creationResponsible,
        estateMain: data.estateMain,
        client: data.client,
        estateManager: data.estateManager,
        emails: data.additionalRecipient
      }));

      const payload = { notifications };

      await put('/notification-emails/panel', payload);
      console.log('Dados salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar os dados:', error);
      warningNotification('Ops!', 'Erro ao salvar os dados');
    } finally {
      setLoading(false);
    }
  };

  return { loading, formData, handleUpdateFormData, handleSave, isBackofficeTest };
};
